import React, { useEffect, Suspense } from "react";
import { render } from "react-dom";
import { Route, Switch } from "react-router-dom";
import { useLocation } from "react-router-dom";
import jwt_decode from "jwt-decode";
import setAuthToken from "./utils/setAuthToken";
import IdleTimer from "./utils/IdleTimer";
import { setCurrentUser, logoutUser } from "./actions/authActions";

import { Provider } from "react-redux";
import store from "./store";
import "./App.css";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
// Transitions temporarily disabled due to bundler ESM/CJS constraints

// Eager mini routes
import Login from "./components/doctor/Login";
import SignUp from "./components/doctor/SignUp";
import Error from "./components/verifier/Error";

// Lazy heavy routes
const Verifier_chart_new = React.lazy(() => import("./components/verifier/Verifier_chart_new"));
const CommonForgetPassword = React.lazy(() => import("./components/common/CommonForgetPassword"));
const ResetPassword = React.lazy(() => import("./components/common/ResetPassword"));
const Distributor = React.lazy(() => import("./components/verifier/Distributor-page"));
const DoctorHome = React.lazy(() => import("./components/doctor/DoctorHome"));
const DoctorProfile = React.lazy(() => import("./components/doctor/DoctorProfile"));
const AdminHome = React.lazy(() => import("./components/admin/AdminHome"));
const DoctorsList = React.lazy(() => import("./components/admin/DoctorsList"));
const VerifiersList = React.lazy(() => import("./components/admin/VerifiersList"));
const AddPatient = React.lazy(() => import("./components/admin/AddPatient"));
const AddVarifier = React.lazy(() => import("./components/admin/AddVarifier"));
const EditVarifier = React.lazy(() => import("./components/admin/EditVarifier"));
const AdminProfile = React.lazy(() => import("./components/admin/AdminProfile"));
const VerifierHome = React.lazy(() => import("./components/verifier/VerifierHome"));
const SupportHome = React.lazy(() => import("./components/verifier/PatientInfo"));
const VerifierProfile = React.lazy(() => import("./components/verifier/VerifierProfile"));
const VerifierChart = React.lazy(() => import("./components/verifier/vc_backup2"));
const VerifierSelectedBeats = React.lazy(() => import("./components/verifier/VerifierSelectedBeats"));
const Vc_backup = React.lazy(() => import("./components/verifier/vc_backup"));
const NewChart = React.lazy(() => import("./components/verifier/NewChart"));
const VerifierUnanalysed = React.lazy(() => import("./components/verifier/VerifierUnanalysed"));
const DoctorUnanalysed = React.lazy(() => import("./components/doctor/DoctorUnanalysed"));
const PatientList = React.lazy(() => import("./components/verifier/patient-list"));
const UserLogin = React.lazy(() => import("./components/user/UserLogin"));
const UserSignUp = React.lazy(() => import("./components/user/UserSignUp"));
const UserHome = React.lazy(() => import("./components/user/UserHome"));
const UserProfile = React.lazy(() => import("./components/user/UserProfile"));
const UserChart = React.lazy(() => import("./components/user/UserChart"));
const ForgetPassword = React.lazy(() => import("./components/user/ForgetPassword"));
const Main = React.lazy(() => import("./components/graph/Main"));
const Testing = React.lazy(() => import("./components/graph/Testing"));
const Unanalysed = React.lazy(() => import("./components/graph/Unanalysed"));
const VerifierDashboard = React.lazy(() => import("./components/Verifier-dashboard/components/verfierDashboard"));
const QC_Home = React.lazy(() => import("./components/verifier/QC-Home"));
const PatientInfoWeb = React.lazy(() => import("./components/verifier/patientInfoWeb"));
const PatientHold = React.lazy(() => import("./components/verifier/Patient-Hold"));
const DeviceDetails = React.lazy(() => import("./components/verifier/Device-details"));
const DeviceDetailsDistributor = React.lazy(() => import("./components/verifier/Distributor-deviceDetails"));
const LabledPatients = React.lazy(() => import("./components/verifier/Labled-patients"));
const removelabelchart = React.lazy(() => import("./components/verifier/removelabelchart"));
const VerificationCompletedList = React.lazy(() => import("./components/verifier/VerificationCompletedList"));
const ReportShared = React.lazy(() => import("./components/verifier/reportShared"));
const ReportPatientsList = React.lazy(() => import("./components/verifier/ReportPatientsList"));
const SupportedPatient = React.lazy(() => import("./components/verifier/supportedPatient"));
const resetPassword = React.lazy(()=>import("./components/Verifier-dashboard/components/ResetPassword"))

const options = {
  title: {
    text: "My chart",
  },
  series: [
    {
      data: [1, 2, 3],
    },
  ],
};

// Check for token
if (localStorage.jwtToken) {
  //Set auth token header auth
  setAuthToken(localStorage.jwtToken);
  //Decode token and get user info and exp
  const decoded = jwt_decode(localStorage.jwtToken);
  // Set user and isAuthenticated
  store.dispatch(setCurrentUser(decoded));

  //check for expired token
  const currentTime = Date.now() / 1000;
  if (decoded.exp < currentTime) {
    //Logout User
    store.dispatch(logoutUser());
    // Redirect to login
    window.location.href = "/";
  }
}

function App() {
  useEffect(() => {
    if (localStorage.jwtToken) {
      new IdleTimer();
    }
  }, []);
  const location = useLocation();
  return (
    <Provider store={store}>
      <Suspense fallback={<div style={{padding:20}}>Loading...</div>}>
          <div key={location.pathname}>
            <Route exact path="/login" component={Login} />
            <Route exact path="/" component={Login} />

            <Route exact path="/signup" component={SignUp} />
            <Route exact path="/user/forgot-password" component={CommonForgetPassword} />

            <Route exact path="/reset-password" component={ResetPassword} />

            <Route exact path="/user-signup" component={UserSignUp} />
            <Route exact path="/user-login" component={UserLogin} />
            <Route exact path="/user-home" component={UserHome} />
            <Route exact path="/user-profile" component={UserProfile} />
            <Route exact path="/user-chart" component={UserChart} />
            <Route exact path="/user-chart/:databaseid" component={UserChart} />
            <Route exact path="/patient/forgot-password" component={ForgetPassword} />

            <Route exact path="/doctor-home" component={DoctorHome} />
            <Route exact path="/doctor-profile" component={DoctorProfile} />
            <Route exact path="/error" component={Error} />

            <Route exact path="/main/:userid" component={Testing} />
            <Route exact path="/main/:userid/:databaseid" component={Testing} />
            <Route exact path="/unanalysed/:userid" component={Unanalysed} />
            <Route exact path="/unanalysed/:userid/:databaseid" component={Unanalysed} />

            <Route exact path="/testing/:userid" component={Testing} />
            <Route exact path="/testing/:userid/:databaseid" component={Testing} />

            <Route exact path="/admin-home" component={AdminHome} />
            <Route exact path="/doctors-list" component={DoctorsList} />
            <Route exact path="/verifiers-list" component={VerifiersList} />
            <Route exact path="/add-patient" component={AddPatient} />
            <Route exact path="/add-varifier" component={AddVarifier} />
            <Route exact path="/edit-varifier/:id" component={EditVarifier} />
            <Route exact path="/admin-profile" component={AdminProfile} />
            <Route exact path="/support-home" component={SupportHome} />
            <Route exact path="/support-homeweb" component={PatientInfoWeb} />
            <Route exact path="/supported-persons" component={SupportedPatient} />
            <Route exact path="/Verification-Completed" component={VerificationCompletedList} />
            <Route exact path="/verifier-home" component={VerifierHome} />
            <Route exact path="/patient-list" component={PatientList} />
            <Route exact path="/Partner" component={Distributor} />
            <Route exact path="/report-shared" component={ReportShared} />

            <Route exact path="/verifier-profile" component={VerifierProfile} />
            <Route exact path="/verifier-dashboard" component={VerifierDashboard} />

            <Route exact path="/verifier-chart/:userid" component={VerifierChart} />
            <Route exact path="/labled-patients" component={LabledPatients} />
            <Route exact path="/verifier-chart/:userid/:databaseid" component={VerifierChart} />
            <Route exact path="/addlabel-chart/:userid/:databaseid" component={Verifier_chart_new} />
            <Route exact path="/ResetPassword" component={resetPassword} />
            <Route exact path="/verifier-chart/:userid/:databaseid/:start/:end/:xValue" component={VerifierChart} />
            <Route exact path="/removelabel-chart/:userid/:databaseid" component={removelabelchart} />
            <Route exact path="/newchart/:userid/:databaseid" component={NewChart} />
            <Route exact path="/verifier-unanalysed/:userid" component={VerifierUnanalysed} />
            <Route exact path="/verifier-unanalysed/:userid/:databaseid" component={VerifierUnanalysed} />
            <Route exact path="/doctor-unanalysed/:userid" component={DoctorUnanalysed} />
            <Route exact path="/doctor-unanalysed/:userid/:databaseid" component={DoctorUnanalysed} />
            <Route exact path="/qc_home" component={QC_Home} />
            <Route exact path="/patient-hold" component={PatientHold} />
            <Route exact path="/device-details" component={DeviceDetails} />
            <Route exact path="/device-home" component={DeviceDetailsDistributor} />
            <Route exact path="/verifier/report-patients-list/:date" component={ReportPatientsList} />
          </div>
      </Suspense>
    </Provider>
  );
}

export default App;
